/**
 * T3-3.3：图案裁剪 Worker poll 共用逻辑（Studio batch / Detail hook / processTask）。
 */
import { pollImageCropBatch } from '../services/imageCropTaskApi';
import { IMAGE_CROP_POLL_INTERVAL_MS, IMAGE_CROP_QWEN_BATCH_SIZE } from '../constants/imageCropConfig';
import { computeCropTaskProgress } from './imageCropTaskStats';
import {
  computeImageCropDetectBatches,
  deriveDetectBatchChunkIndex,
} from './imageCropStudioProgress';
import { isImageCropFatalPollError } from './imageCropTaskApiError';

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** 轮询间隔内分段 sleep，便于及时响应取消 */
export async function sleepImageCropPollInterval(ms, shouldAbort) {
  const stepMs = 250;
  let elapsed = 0;
  while (elapsed < ms) {
    if (shouldAbort?.()) return false;
    const chunk = Math.min(stepMs, ms - elapsed);
    await sleep(chunk);
    elapsed += chunk;
  }
  return !shouldAbort?.();
}

/**
 * 分页拉取任务全部裁剪记录；取消时返回 null。
 * @param {string} taskId
 * @param {{ shouldAbort?: () => boolean }} [options]
 * @returns {Promise<Array<object> | null>}
 */
export async function fetchAllImageCropPollRows(taskId, { shouldAbort } = {}) {
  const merged = [];
  let page = 1;
  let totalPages = 1;
  while (page <= totalPages) {
    if (shouldAbort?.()) return null;
    const batch = await pollImageCropBatch(taskId, page);
    if (shouldAbort?.()) return null;
    merged.push(...(batch.images || []));
    totalPages = Math.max(1, Number(batch.totalPages) || 1);
    page += 1;
  }
  return merged;
}

/**
 * @param {string} taskId
 * @param {{ shouldAbort?: () => boolean }} [options]
 * @returns {Promise<Map<string, object> | null>}
 */
export async function fetchAllImageCropPollRowMap(taskId, { shouldAbort } = {}) {
  const rows = await fetchAllImageCropPollRows(taskId, { shouldAbort });
  if (rows === null) return null;
  const map = new Map();
  rows.forEach((row) => {
    if (row?.imageId != null) map.set(String(row.imageId), row);
  });
  return map;
}

/**
 * @param {Array<object>} rows
 * @param {string[]} imageIds
 * @param {{ detectBatches?: number }} [options]
 */
export function buildImageCropPollSnapshot(rows, imageIds, { detectBatches } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const ids = [...new Set((imageIds || []).filter(Boolean).map(String))];
  const progress = computeCropTaskProgress(list, ids);
  const batches = Number(detectBatches) || computeImageCropDetectBatches(ids.length);
  const detectBatchIndex = deriveDetectBatchChunkIndex(
    list,
    ids,
    IMAGE_CROP_QWEN_BATCH_SIZE,
  );
  return {
    rows: list,
    imageIds: ids,
    ...progress,
    detectBatches: batches,
    detectBatchIndex,
    done: progress.pendingCount <= 0,
  };
}

/**
 * 执行一次 poll（全部分页）。
 * @returns {Promise<object | null>} null 表示已取消
 */
export async function pollImageCropTaskOnce(taskId, imageIds, options = {}) {
  const rows = await fetchAllImageCropPollRows(taskId, options);
  if (rows === null) return null;
  return buildImageCropPollSnapshot(rows, imageIds, options);
}

/**
 * 持续 poll 直至全部终态或取消。
 * @returns {Promise<{ aborted: boolean, snapshot: object | null, stopped?: boolean }>}
 */
export async function runImageCropPollLoop({
  taskId,
  imageIds,
  onTick,
  shouldAbort,
  intervalMs = IMAGE_CROP_POLL_INTERVAL_MS,
  detectBatches,
  maxTicks = 600,
} = {}) {
  let ticks = 0;
  while (true) {
    if (shouldAbort?.()) {
      return { aborted: true, snapshot: null };
    }

    let snapshot;
    try {
      snapshot = await pollImageCropTaskOnce(taskId, imageIds, { shouldAbort, detectBatches });
    } catch (error) {
      if (isImageCropFatalPollError(error) || ticks >= maxTicks) {
        throw error;
      }
      ticks += 1;
      const shouldContinueAfterError = await sleepImageCropPollInterval(intervalMs, shouldAbort);
      if (!shouldContinueAfterError) {
        return { aborted: true, snapshot: null };
      }
      continue;
    }

    if (snapshot === null) {
      return { aborted: true, snapshot: null };
    }

    await onTick?.(snapshot);

    if (snapshot.done) {
      return { aborted: false, snapshot };
    }

    ticks += 1;
    if (ticks >= maxTicks) {
      return { aborted: false, snapshot, stopped: true };
    }

    const shouldContinue = await sleepImageCropPollInterval(intervalMs, shouldAbort);
    if (!shouldContinue) {
      return { aborted: true, snapshot: null };
    }
  }
}
